import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, Carousel, SvgIcon } from '@gik/ui';
import i18n from '@gik/i18n';
import coreTranslationKeys from '@gik/i18n/en/core';
import { openIntercomWindow } from '@gik/core/utils/Intercom';
import { useCreateFlowStore } from '@gik/create/store/CreateFlowStore';
import { CreatePageContext } from '@gik/create/components/CreatePage/CreatePage';
import { IWordpressCreateFlowCategory } from '@gik/core/models/wordpress/WordpressSituation';
import { translationKeys } from '@gik/auth/components/Signup/i18n/en';
import { EmailTakenStatus } from '@gik/create/enums/EmailTakenStatus';

export interface ICreatePageNavigationProps {
  stepIndex: number;
  className?: string;
  hasNext?: boolean;
  trackIntercomClicked?: () => void;
}

const blockName = 'create-page-navigation';

export default function CreatePageNavigation({
  stepIndex,
  className,
  hasNext,
  trackIntercomClicked,
}: ICreatePageNavigationProps) {
  const bem = bemBlock(blockName);
  const nextDisabled = useCreateFlowStore(state => state.nextDisabled);
  const finishButtonSubmitting = useCreateFlowStore(state => state.finishButtonSubmitting);
  const { handleBack, handleNext, handleFinish } = React.useContext(CreatePageContext);
  const { footerSituationCarouselRef } = React.useContext(CreatePageContext);
  const situationCarouselRef = useCreateFlowStore(state => state.situationCarouselRef);
  const emailTakenStatus = useCreateFlowStore(state => state.emailTakenStatus);

  const selectedCategoryIndex = useCreateFlowStore(state => state.selectedCategoryIndex);
  const situationCategories = useCreateFlowStore(state => state.situationCategories);

  const [categories, setCategories] = React.useState<IWordpressCreateFlowCategory[]>();

  function handleOpenIntercom() {
    trackIntercomClicked?.();
    openIntercomWindow();
  }

  React.useEffect(() => {
    const matchingCategories: IWordpressCreateFlowCategory[] = situationCategories?.map((item, index) => ({
      ...item,
      index,
    }));
    setCategories(matchingCategories);
  }, [situationCategories, setCategories]);

  return (
    <div className={bem('wrapper', [{ 'has-dots': stepIndex === 1 }])}>
      {situationCarouselRef && categories && stepIndex === 1 && (
        <Carousel
          initialSlide={selectedCategoryIndex}
          variant="primary"
          asNavFor={situationCarouselRef}
          dots={true}
          arrows={false}
          ref={footerSituationCarouselRef}
        >
          {categories.map(category => (
            <div key={category.value}>1</div>
          ))}
        </Carousel>
      )}
      <div className={bem(null, null, className)}>
        <div className={bem('left-group')}>
          <Button variant={'default-light'} onClick={handleOpenIntercom} circle>
            <SvgIcon name={'InterrogationRed'} set={'gik-icons'} />
          </Button>
        </div>

        <div className={bem('right-group')}>
          <Button
            className={bem('btn-back')}
            variant={'default-light'}
            onClick={handleBack}
            disabled={stepIndex <= 0}
            prepend={<SvgIcon name={'ChevronLeft'} />}
          >
            {i18n.t(coreTranslationKeys.core.back)}
          </Button>
          {hasNext && (
            <Button
              className={bem('btn-next')}
              onClick={handleNext}
              disabled={nextDisabled}
              append={<SvgIcon name={'ChevronRight'} />}
            >
              {i18n.t(coreTranslationKeys.core.next)}
            </Button>
          )}
          {!hasNext && (
            <Button onClick={handleFinish} disabled={nextDisabled} loading={finishButtonSubmitting}>
              {i18n.t(
                emailTakenStatus === EmailTakenStatus.FREE
                  ? translationKeys.formSubmitButton
                  : coreTranslationKeys.core.finish
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
